import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastContainer } from './components/Toast';
import { ToastProvider } from './contexts/ToastContext';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/client/DashboardPage';
import { ApiKeysPage } from './pages/client/ApiKeysPage';
import { TemplatesPage } from './pages/client/TemplatesPage';
import { TestLabelPage } from './pages/client/TestLabelPage';
import { UsagePage } from './pages/client/UsagePage';
import { DocsPage } from './pages/client/DocsPage';
import { RetailersPage } from './pages/client/RetailersPage';
import { RetailerDetailPage } from './pages/client/RetailerDetailPage';
import { TemplateDetailPage } from './pages/client/TemplateDetailPage';
import { AdminTenantsPage } from './pages/admin/AdminTenantsPage';
import { AdminTemplatesPage } from './pages/admin/AdminTemplatesPage';
import { AdminBillingPage } from './pages/admin/AdminBillingPage';
import { AdminMonitoringPage } from './pages/admin/AdminMonitoringPage';
import { AdminRetailersPage } from './pages/admin/AdminRetailersPage';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { AdminTenantDetailPage } from './pages/admin/AdminTenantDetailPage';
import { AdminOnboardingPage } from './pages/admin/AdminOnboardingPage';
import { OnboardingJobDetailPage } from './pages/admin/OnboardingJobDetailPage';
import { AdminHelpPage } from './pages/admin/AdminHelpPage';
import { AdminRendererComparePage } from './pages/admin/AdminRendererComparePage';
import { AdminLabelFeedbackPage } from './pages/admin/AdminLabelFeedbackPage';

function ProtectedRoute({ children, requiresAdmin = false }: { children: React.ReactNode; requiresAdmin?: boolean }) {
  const { isAuthenticated, isStaff, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiresAdmin && !isStaff) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
        
          {/* Client Routes */}
          <Route path="/" element={
            <ProtectedRoute requiresAdmin={false}>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<DashboardPage />} />
            <Route path="api-keys" element={<ApiKeysPage />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="retailers" element={<RetailersPage />} />
            <Route path="retailers/:retailerId" element={<RetailerDetailPage />} />
            <Route path="retailers/:retailerId/templates/:templateKey" element={<TemplateDetailPage />} />
            <Route path="test-label" element={<TestLabelPage />} />
            <Route path="usage" element={<UsagePage />} />
            <Route path="docs" element={<DocsPage />} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute requiresAdmin>
              <Layout isAdmin />
            </ProtectedRoute>
          }>
            <Route index element={<ErrorBoundary><AdminDashboardPage /></ErrorBoundary>} />
            <Route path="tenants" element={<ErrorBoundary><AdminTenantsPage /></ErrorBoundary>} />
            <Route path="tenants/:tenantId" element={<ErrorBoundary><AdminTenantDetailPage /></ErrorBoundary>} />
            <Route path="retailers" element={<ErrorBoundary><AdminRetailersPage /></ErrorBoundary>} />
            <Route path="onboarding" element={<ErrorBoundary><AdminOnboardingPage /></ErrorBoundary>} />
            <Route path="onboarding/:jobId" element={<ErrorBoundary><OnboardingJobDetailPage /></ErrorBoundary>} />
            <Route path="templates" element={<ErrorBoundary><AdminTemplatesPage /></ErrorBoundary>} />
            <Route path="label-feedback" element={<ErrorBoundary><AdminLabelFeedbackPage /></ErrorBoundary>} />
            <Route path="billing" element={<ErrorBoundary><AdminBillingPage /></ErrorBoundary>} />
            <Route path="monitoring" element={<ErrorBoundary><AdminMonitoringPage /></ErrorBoundary>} />
            <Route path="renderer-compare" element={<ErrorBoundary><AdminRendererComparePage /></ErrorBoundary>} />
            <Route path="help" element={<ErrorBoundary><AdminHelpPage /></ErrorBoundary>} />
          </Route>
        </Routes>
        <ToastContainer />
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
